import type { HttpRequest } from '../http.js'

const DEFAULT_OLLAMA_HOST = 'localhost:11434'

export interface OllamaRequestParams {
  host?: string
  path: string
  method?: string
  body?: Record<string, unknown>
  timeout?: number
}

export type OllamaRequestAPI = (params: OllamaRequestParams) => Promise<unknown>

export interface OllamaUtils {
  requestAPI: OllamaRequestAPI
  checkAvailability: (host?: string) => Promise<boolean>
}

export function createOllamaUtils(httpRequest: HttpRequest): OllamaUtils {
  const requestAPI: OllamaRequestAPI = async ({
    host = DEFAULT_OLLAMA_HOST,
    path,
    method = 'POST',
    body = {},
    timeout = 300000,
  }) => {
    const [hostname, port] = host.split(':')

    const options = {
      protocol: 'http' as const,
      options: {
        host: hostname,
        port: Number.parseInt(port || '11434', 10),
        path,
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        timeout,
      },
      data: method === 'POST' || method === 'PUT' ? JSON.stringify(body) : '',
    }

    try {
      const resp = await httpRequest(options)

      if (resp.statusCode >= 400) {
        throw new Error(`Ollama API error: ${resp.statusCode} - ${JSON.stringify(resp.body)}`)
      }

      return JSON.parse(resp.body)
    } catch (error: any) {
      if (error.code === 'ECONNREFUSED' || error.message?.includes('connect')) {
        throw new Error('Cannot connect to Ollama. Make sure Ollama is running (try: ollama serve)')
      }
      throw error
    }
  }

  const checkAvailability = async (host: string = DEFAULT_OLLAMA_HOST): Promise<boolean> => {
    try {
      await requestAPI({ host, path: '/api/tags', method: 'GET', timeout: 3000 })
      return true
    } catch (_error) {
      return false
    }
  }

  return { requestAPI, checkAvailability }
}
